import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Calendar, Flame, Target, Award, Activity, Ruler } from 'lucide-react';

const Profile = () => {
  const navigate = useNavigate();
  const { user, totals, exerciseTotal, goals } = useApp();
  const wu = user.weightUnit;

  const stats = [
    { label: 'Streak', value: `${user.streak}d`, icon: Flame, color: 'text-orange-500' },
    { label: 'Change', value: `${(user.currentWeight - user.startWeight).toFixed(1)} ${wu}`, icon: Award, color: 'text-emerald-600' },
    { label: 'Cal Today', value: totals.calories.toLocaleString(), icon: Activity, color: 'text-[#0066ee]' },
    { label: 'Burned', value: exerciseTotal.toLocaleString(), icon: Target, color: 'text-purple-600' },
  ];

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">
      <header className="bg-[#0066ee] text-white px-5 pt-5 pb-16 relative">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/10">
          <ArrowLeft size={22} />
        </button>
        <h1 className="mt-1 text-xl font-bold">Profile</h1>
      </header>

      <div className="px-5 -mt-12">
        <div className="bg-white rounded-2xl shadow-md p-5 flex flex-col items-center text-center">
          <img src={user.avatar} alt={user.name} className="w-20 h-20 rounded-full border-4 border-white shadow-lg object-cover -mt-12" />
          <h2 className="mt-3 text-lg font-bold text-gray-900">{user.name}</h2>
          <p className="text-xs text-gray-500">{user.username}</p>
          <span className="mt-2 px-3 py-1 bg-blue-50 text-[#0066ee] rounded-full text-xs font-semibold">
            {user.goal}
          </span>
          <div className="mt-4 grid grid-cols-4 gap-2 w-full">
            {stats.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="flex flex-col items-center">
                  <Icon size={18} className={s.color} />
                  <p className="text-sm font-bold text-gray-900 mt-1">{s.value}</p>
                  <p className="text-[10px] text-gray-500 uppercase">{s.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <section className="px-5 mt-4">
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <p className="text-sm font-bold text-gray-900 mb-3">Stats</p>
          <ul className="space-y-3">
            <Row icon={<Ruler size={16} className="text-gray-500" />} label="Height" value={user.height} />
            <Row icon={<Activity size={16} className="text-gray-500" />} label="Age" value={`${user.age} yrs`} />
            <Row icon={<Target size={16} className="text-gray-500" />} label="Goal Weight" value={`${user.goalWeight} ${wu}`} />
            <Row icon={<Target size={16} className="text-gray-500" />} label="Start Weight" value={`${user.startWeight} ${wu}`} />
            <Row icon={<Calendar size={16} className="text-gray-500" />} label="Joined" value={user.joinedDate} />
          </ul>
        </div>
      </section>

      <section className="px-5 mt-4 pb-6">
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <p className="text-sm font-bold text-gray-900 mb-3">Daily Goals</p>
          <div className="grid grid-cols-2 gap-3">
            <GoalCell label="Calories" value={`${goals.calories.toLocaleString()} cal`} />
            <GoalCell label="Carbs" value={`${goals.carbs} g`} />
            <GoalCell label="Protein" value={`${goals.protein} g`} />
            <GoalCell label="Fat" value={`${goals.fat} g`} />
            <GoalCell label="Water" value={`${goals.water} cups`} />
          </div>
        </div>
      </section>
    </div>
  );
};

const Row = ({ icon, label, value }) => (
  <li className="flex items-center justify-between">
    <div className="flex items-center gap-2">{icon}<span className="text-sm text-gray-700">{label}</span></div>
    <span className="text-sm font-semibold text-gray-900">{value}</span>
  </li>
);

const GoalCell = ({ label, value }) => (
  <div className="bg-gray-50 rounded-xl p-3">
    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
    <p className="text-sm font-bold text-gray-900 mt-0.5">{value}</p>
  </div>
);

export default Profile;
